//SCR-052 (S2.4.7): drawer progressive disclosure — preview quyền hiệu lực và lịch sử
//phân quyền chỉ fetch/xuất hiện khi người dùng mở (IRM §8.2 PROGRESSIVE). Action
//trình bày bằng tên nghiệp vụ; warnings/limitations là nội dung bắt buộc để ngăn
//quyết định sai, không được ẩn.
import { Alert, Drawer, Empty, Skeleton, Tabs, Tag, Typography } from "antd";
import {
  ASSIGNMENT_BADGE_STATUS,
  ASSIGNMENT_STATE_LABEL,
  describeAction,
  formatAssignmentDate,
} from "./roleAssignmentAdminApi";
import type {
  CatalogRoleOption,
  EffectivePermissionsPreview,
  RoleAssignmentItem,
} from "./roleAssignmentAdminApi";
import { RoleAssignmentTable } from "./RoleAssignmentTable";
import type { AssignmentState } from "./roleAssignmentAdminApi";

export type DrawerMode = "preview" | "history";

export type PreviewState =
  | Readonly<{ status: "loading" }>
  | Readonly<{ status: "error"; message: string; requestId?: string | undefined }>
  | Readonly<{ status: "ready"; preview: EffectivePermissionsPreview }>;

export function EffectivePermissionDrawer({
  open,
  mode,
  accountLabel,
  previewState,
  historyItems,
  catalogRoles,
  orgNames,
  areaNames,
  onClose,
}: {
  readonly open: boolean;
  readonly mode: DrawerMode;
  readonly accountLabel: string;
  readonly previewState: PreviewState | null;
  readonly historyItems: readonly RoleAssignmentItem[];
  readonly catalogRoles: readonly CatalogRoleOption[];
  readonly orgNames: ReadonlyMap<string, string>;
  readonly areaNames: ReadonlyMap<string, string>;
  readonly onClose: () => void;
}) {
  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={560}
      title={`Phân quyền của ${accountLabel}`}
      destroyOnClose
    >
      <Tabs
        defaultActiveKey={mode}
        items={[
          {
            key: "preview",
            label: "Quyền hiệu lực",
            children: <PreviewPanel state={previewState} />,
          },
          {
            key: "history",
            label: "Lịch sử phân quyền",
            children: (
              <RoleAssignmentTable
                items={historyItems}
                catalogRoles={catalogRoles}
                orgNames={orgNames}
                areaNames={areaNames}
                canRevoke={false}
                onRevoke={() => undefined}
              />
            ),
          },
        ]}
      />
    </Drawer>
  );
}

function PreviewPanel({ state }: { readonly state: PreviewState | null }) {
  if (state == null) {
    return <Empty description="Chưa tải quyền hiệu lực" />;
  }
  if (state.status === "loading") {
    return <Skeleton active paragraph={{ rows: 6 }} />;
  }
  if (state.status === "error") {
    return (
      <Alert
        type="error"
        showIcon
        role="alert"
        message={state.message}
        description={state.requestId ? `Mã hỗ trợ: ${state.requestId}` : undefined}
      />
    );
  }
  const { preview } = state;
  if (preview.grants.length === 0) {
    return (
      <>
        <Empty description="Chưa có vai trò đang hiệu lực" />
        <LimitationList limitations={preview.limitations} />
      </>
    );
  }
  return (
    <div className="role-assignment-preview-list">
      {preview.grants.map((grant) => (
        <GrantCard
          key={grant.assignmentId}
          grant={grant}
        />
      ))}
      <LimitationList limitations={preview.limitations} />
    </div>
  );
}

function GrantCard({ grant }: { readonly grant: EffectivePermissionsPreview["grants"][number] }) {
  const status = grant.status as AssignmentState;
  return (
    <article className="role-assignment-preview-card">
      <header>
        <Typography.Text strong>{grant.roleDisplayName}</Typography.Text>
        <Tag color={ASSIGNMENT_BADGE_STATUS[status] ?? "default"}>
          {ASSIGNMENT_STATE_LABEL[status] ?? "Không xác định"}
        </Tag>
      </header>
      <Typography.Text type="secondary">
        {[grant.organizationName, grant.administrativeAreaName]
          .filter((value): value is string => Boolean(value))
          .join(" · ") || "Không giới hạn phạm vi"}
        {" — "}
        {formatAssignmentDate(grant.validFrom)} → {grant.validTo ? formatAssignmentDate(grant.validTo) : "vô thời hạn"}
      </Typography.Text>
      <div className="role-assignment-preview-actions">
        {grant.actionCodes.map((code) => (
          <Tag key={code}>{describeAction(code)}</Tag>
        ))}
      </div>
      {grant.warnings.map((warning) => (
        <Alert key={warning} type="warning" showIcon message={warning} />
      ))}
    </article>
  );
}

function LimitationList({ limitations }: { readonly limitations: readonly string[] }) {
  if (limitations.length === 0) {
    return null;
  }
  return (
    <Alert
      type="info"
      showIcon
      message="Hạn chế theo chính sách hiện hành"
      description={
        <ul>
          {limitations.map((limitation) => <li key={limitation}>{limitation}</li>)}
        </ul>
      }
    />
  );
}
